// src/pages/WHSInspections.tsx — HSEQ Inspections & Issue Tracking
// Follows frontend-skill.md conventions exactly:
//   - Functional components + hooks only
//   - Tailwind utility classes only
//   - Primary: indigo-600  Success: green-500  Warning: amber-500  Danger: red-500
//   - Neutrals: slate-100 through slate-900
//   - Page wrapper: <div className="p-6 space-y-6">

import { useState, useMemo, useEffect } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_WHS_INSPECTIONS, WHSInspection, InspectionIssue,
  InspectionType, InspectionResult, IssueSeverity, IssueStatus,
} from '../utils/mockDataWHS'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import { MOCK_JOBS } from '../utils/mockData'
import { formatDate, formatDateTime, formatRelative } from '../utils/formatters'
import { useQuery } from '@tanstack/react-query'
import { fetchWHSInspections } from '../services/whsInspectionsService'
import {
  Search, Plus, ArrowLeft, X, CheckCircle, AlertTriangle, Clock,
  ChevronRight, ClipboardList, ShieldAlert, ThumbsUp, UserCheck,
  Clipboard, Camera, ChevronDown, ChevronUp, MapPin, Calendar,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────
// CONSTANTS & META
// ─────────────────────────────────────────────────────────────────

const INSPECTION_TYPE_META: Record<InspectionType, { label: string; icon: string; bg: string; text: string }> = {
  DAILY_PRESTART:         { label: 'Daily pre-start',        icon: '🌅', bg: 'bg-sky-50',    text: 'text-sky-700'    },
  SAFETY_WALK:            { label: 'Safety walk',            icon: '🦺', bg: 'bg-green-50',  text: 'text-green-700'  },
  PLANT_INSPECTION:       { label: 'Plant inspection',       icon: '🚜', bg: 'bg-amber-50',  text: 'text-amber-700'  },
  ELECTRICAL_INSTALL:     { label: 'Electrical install',     icon: '⚡', bg: 'bg-yellow-50', text: 'text-yellow-700' },
  EXCAVATION_CHECK:       { label: 'Excavation check',       icon: '🏗', bg: 'bg-orange-50', text: 'text-orange-700' },
  CONFINED_SPACE_PREENTRY:{ label: 'Confined space pre-entry', icon: '🕳', bg: 'bg-purple-50', text: 'text-purple-700' },
  WEEKLY_SITE_AUDIT:      { label: 'Weekly site audit',      icon: '📋', bg: 'bg-indigo-50', text: 'text-indigo-700' },
}

const RESULT_META: Record<InspectionResult | 'PENDING', { label: string; bg: string; text: string; dot: string }> = {
  PASS:        { label: 'Pass',        bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  CONDITIONAL: { label: 'Conditional', bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  FAIL:        { label: 'Fail',        bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  PENDING:     { label: 'In progress', bg: 'bg-slate-100',  text: 'text-slate-600',  dot: 'bg-slate-400'  },
}

const SEVERITY_META: Record<IssueSeverity, { label: string; bg: string; text: string; border: string }> = {
  LOW:      { label: 'Low',      bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200'   },
  MEDIUM:   { label: 'Medium',   bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200'  },
  HIGH:     { label: 'High',     bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  CRITICAL: { label: 'Critical', bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200'    },
}

const ISSUE_STATUS_META: Record<IssueStatus, { label: string; bg: string; text: string }> = {
  OPEN:        { label: 'Open',        bg: 'bg-red-100',    text: 'text-red-700'    },
  IN_PROGRESS: { label: 'In progress', bg: 'bg-amber-100',  text: 'text-amber-700'  },
  CLOSED:      { label: 'Closed',      bg: 'bg-green-100',  text: 'text-green-700'  },
}

// ─────────────────────────────────────────────────────────────────
// CHECKLIST TEMPLATES (pre-built per inspection type)
// ─────────────────────────────────────────────────────────────────

type ChecklistTemplate = { id: string; label: string; category: string }

const CHECKLISTS: Record<InspectionType, ChecklistTemplate[]> = {
  DAILY_PRESTART: [
    { id: 'dp1',  label: 'PPE worn correctly by all crew (hi-vis, boots, hard hat, glasses)', category: 'PPE' },
    { id: 'dp2',  label: 'Traffic Management in place and correct per approved TMP', category: 'Traffic' },
    { id: 'dp3',  label: 'All open excavations barricaded and/or covered', category: 'Excavation' },
    { id: 'dp4',  label: 'Plant pre-start checks completed and logged', category: 'Plant' },
    { id: 'dp5',  label: 'SWMS on site and accessible to all workers', category: 'Safety Docs' },
    { id: 'dp6',  label: 'Emergency contacts posted and clearly visible', category: 'Emergency' },
    { id: 'dp7',  label: 'First aid kit stocked and location known to all', category: 'Emergency' },
    { id: 'dp8',  label: 'Fire extinguisher in place (fabrication/hot works area)', category: 'Fire' },
    { id: 'dp9',  label: 'Site compound tidy — no trip/slip hazards', category: 'Housekeeping' },
    { id: 'dp10', label: 'All workers hold current induction for this site', category: 'Induction' },
  ],
  SAFETY_WALK: [
    { id: 'sw1',  label: 'Perimeter barricading secure and complete', category: 'Access Control' },
    { id: 'sw2',  label: 'All PPE requirements observed across site', category: 'PPE' },
    { id: 'sw3',  label: 'Traffic management setup correct per TMP', category: 'Traffic' },
    { id: 'sw4',  label: 'Excavations and open pits adequately protected', category: 'Excavation' },
    { id: 'sw5',  label: 'Electrical isolations and LOTO in place where required', category: 'Electrical' },
    { id: 'sw6',  label: 'Housekeeping acceptable — walkways clear', category: 'Housekeeping' },
    { id: 'sw7',  label: 'Materials stacked and stored safely', category: 'Housekeeping' },
    { id: 'sw8',  label: 'Plant operators maintaining safe clearances', category: 'Plant' },
    { id: 'sw9',  label: 'SWMS visible and being followed', category: 'Safety Docs' },
    { id: 'sw10', label: 'No unauthorised persons in work zone', category: 'Access Control' },
    { id: 'sw11', label: 'Environmental controls in place (spill kit, dust suppression)', category: 'Environment' },
    { id: 'sw12', label: 'Toolbox talk or pre-start meeting held this morning', category: 'Communication' },
  ],
  PLANT_INSPECTION: [
    { id: 'pi1',  label: 'Plant registration / licence displayed and current', category: 'Compliance' },
    { id: 'pi2',  label: 'Operator holds valid HRW licence for this plant', category: 'Compliance' },
    { id: 'pi3',  label: 'Pre-start checklist completed and signed', category: 'Compliance' },
    { id: 'pi4',  label: 'Seatbelt functional and being worn', category: 'Safety' },
    { id: 'pi5',  label: 'All guards and protective devices in place', category: 'Safety' },
    { id: 'pi6',  label: 'Lights, horn, reversing alarm operational', category: 'Safety' },
    { id: 'pi7',  label: 'No visible oil or fluid leaks', category: 'Mechanical' },
    { id: 'pi8',  label: 'Tyres / tracks in serviceable condition', category: 'Mechanical' },
    { id: 'pi9',  label: 'Fire extinguisher mounted and accessible on plant', category: 'Fire' },
    { id: 'pi10', label: 'Spill kit available on/near plant', category: 'Environment' },
  ],
  ELECTRICAL_INSTALL: [
    { id: 'ei1',  label: 'Isolation confirmed — test-before-touch performed', category: 'Electrical' },
    { id: 'ei2',  label: 'LOTO applied — lock and tag on all isolation points', category: 'Electrical' },
    { id: 'ei3',  label: 'Earth clamps applied before work', category: 'Electrical' },
    { id: 'ei4',  label: 'Arc flash PPE worn (category appropriate to voltage)', category: 'PPE' },
    { id: 'ei5',  label: 'Insulated tools rated for voltage in use', category: 'Tools' },
    { id: 'ei6',  label: 'Exclusion zone established around energised equipment', category: 'Access' },
    { id: 'ei7',  label: 'Safety observer assigned and in position', category: 'Safety' },
    { id: 'ei8',  label: 'Emergency rescue plan confirmed (CPR/AED location)', category: 'Emergency' },
    { id: 'ei9',  label: 'Cable containment and terminations to spec', category: 'Quality' },
    { id: 'ei10', label: 'All cable joints and terminations labelled correctly', category: 'Quality' },
  ],
  EXCAVATION_CHECK: [
    { id: 'ex1',  label: 'BYDA plans on site and current', category: 'Documentation' },
    { id: 'ex2',  label: 'Services located and marked before excavation', category: 'Services' },
    { id: 'ex3',  label: 'Hand-dig zone (500mm from any service) observed', category: 'Services' },
    { id: 'ex4',  label: 'Spotter in position for mechanical excavation', category: 'Personnel' },
    { id: 'ex5',  label: 'Excavation depth appropriate — no unsupported over 1.5m', category: 'Stability' },
    { id: 'ex6',  label: 'Battering or shoring in place where required', category: 'Stability' },
    { id: 'ex7',  label: 'Spoil kept clear of excavation edge (min 600mm)', category: 'Stability' },
    { id: 'ex8',  label: 'All open excavations barricaded when unattended', category: 'Access Control' },
    { id: 'ex9',  label: 'Dewatering in place if groundwater present', category: 'Stability' },
    { id: 'ex10', label: 'Conduit installation depth confirmed before backfill', category: 'Quality' },
  ],
  CONFINED_SPACE_PREENTRY: [
    { id: 'cs1',  label: 'Atmospheric test: O₂ reading 19.5–23.5%', category: 'Atmosphere' },
    { id: 'cs2',  label: 'CO reading < 25 ppm', category: 'Atmosphere' },
    { id: 'cs3',  label: 'H₂S reading < 1 ppm', category: 'Atmosphere' },
    { id: 'cs4',  label: 'Methane / LEL reading < 10%', category: 'Atmosphere' },
    { id: 'cs5',  label: 'Atmospheric test within last 4 hours', category: 'Atmosphere' },
    { id: 'cs6',  label: 'Forced ventilation running (if required by risk assessment)', category: 'Ventilation' },
    { id: 'cs7',  label: 'Standby person stationed outside — will NOT enter', category: 'Personnel' },
    { id: 'cs8',  label: 'Rescue equipment on hand and accessible', category: 'Emergency' },
    { id: 'cs9',  label: 'Communications established (radio/phone)', category: 'Communications' },
    { id: 'cs10', label: 'Confined space entry permit approved and on site', category: 'Documentation' },
    { id: 'cs11', label: 'All entrants hold confined space certificate', category: 'Compliance' },
  ],
  WEEKLY_SITE_AUDIT: [
    { id: 'wa1',  label: 'Site induction records up to date for all workers', category: 'Induction' },
    { id: 'wa2',  label: 'SWMS current and signed by all active workers', category: 'Safety Docs' },
    { id: 'wa3',  label: 'All open PTW (Permits to Work) properly authorised', category: 'Permits' },
    { id: 'wa4',  label: 'Previous week inspection issues closed out', category: 'Issue Tracking' },
    { id: 'wa5',  label: 'Incident register reviewed — all incidents investigated', category: 'Incidents' },
    { id: 'wa6',  label: 'Plant inspection and service records current', category: 'Plant' },
    { id: 'wa7',  label: 'Traffic Management Plans current and re-approved if changed', category: 'Traffic' },
    { id: 'wa8',  label: 'Environmental controls adequate — no breaches this week', category: 'Environment' },
    { id: 'wa9',  label: 'Toolbox talks conducted and attendance recorded', category: 'Communication' },
    { id: 'wa10', label: 'Subcontractor compliance documents current (insurance, licences)', category: 'Compliance' },
    { id: 'wa11', label: 'Site first aid resources adequate and restocked', category: 'Emergency' },
    { id: 'wa12', label: 'Emergency plan reviewed with site crew', category: 'Emergency' },
  ],
}

// ─────────────────────────────────────────────────────────────────
// SMALL COMPONENTS
// ─────────────────────────────────────────────────────────────────

function ResultBadge({ result }: { result: InspectionResult | null }) {
  const m = RESULT_META[result ?? 'PENDING']
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  )
}

function SeverityBadge({ severity }: { severity: IssueSeverity }) {
  const m = SEVERITY_META[severity]
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.bg} ${m.text} ${m.border}`}>
      {m.label}
    </span>
  )
}

function ItemResultIcon({ result }: { result: string | null }) {
  if (result === 'OK')    return <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
  if (result === 'ISSUE') return <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
  if (result === 'NA')    return <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-xs font-bold text-slate-300">N/A</span>
  return <span className="w-5 h-5 flex-shrink-0 rounded-full border-2 border-slate-200" />
}

// ─────────────────────────────────────────────────────────────────
// ISSUE CLOSE-OUT MODAL
// ─────────────────────────────────────────────────────────────────

function CloseOutModal({
  issue, onClose, onSubmit,
}: {
  issue: InspectionIssue
  onClose: () => void
  onSubmit: (id: string, notes: string) => void
}) {
  const [notes, setNotes] = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Close out issue</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Issue summary */}
          <div className={`rounded-xl p-4 border ${SEVERITY_META[issue.severity].border} ${SEVERITY_META[issue.severity].bg}`}>
            <div className="flex items-start gap-2">
              <SeverityBadge severity={issue.severity} />
            </div>
            <p className="text-sm font-medium text-slate-800 mt-2">{issue.description}</p>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {issue.location}
            </p>
            {issue.assignedToName && (
              <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                <UserCheck className="w-3 h-3" /> Assigned to {issue.assignedToName}
              </p>
            )}
          </div>

          {/* Close-out notes */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Close-out notes <span className="text-red-500">*</span>
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Describe the corrective action taken and current condition of the area..."
              className="input w-full resize-none h-28 text-sm"
              autoFocus
            />
          </div>

          <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-500">
            Photo evidence may be required. In production, attach a photo before closing critical issues.
          </div>
        </div>
        <div className="px-6 py-4 border-t flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={() => { if (notes.trim()) { onSubmit(issue.id, notes); onClose() } }}
            disabled={!notes.trim()}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-40 transition-colors"
          >
            <CheckCircle className="w-4 h-4" /> Mark closed
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// CONDUCT INSPECTION MODAL
// ─────────────────────────────────────────────────────────────────

type ItemResult = 'OK' | 'ISSUE' | 'NA' | null

interface InlineIssue {
  description: string
  severity: IssueSeverity
  location: string
  assignedToId: string
}

interface ChecklistItemState extends ChecklistTemplate {
  result: ItemResult
  issue?: InlineIssue
  showIssueForm: boolean
}

function ConductInspectionModal({
  onClose, onSubmit,
}: {
  onClose: () => void
  onSubmit: (inspection: WHSInspection) => void
}) {
  const { user } = useAuthStore()

  // Step 1: setup
  const [step, setStep]           = useState<'setup' | 'checklist'>('setup')
  const [type, setType]           = useState<InspectionType>('DAILY_PRESTART')
  const [jobId, setJobId]         = useState(MOCK_JOBS[0]?.id ?? '')

  // Step 2: checklist
  const [items, setItems]         = useState<ChecklistItemState[]>([])
  const [positives, setPositives] = useState('')
  const [overallNotes, setOverallNotes] = useState('')
  const [expandedItem, setExpandedItem] = useState<string | null>(null)

  const handleStartChecklist = () => {
    const template = CHECKLISTS[type] ?? []
    setItems(template.map(t => ({ ...t, result: null, showIssueForm: false })))
    setStep('checklist')
  }

  const setItemResult = (id: string, result: ItemResult) => {
    setItems(prev => prev.map(i => {
      if (i.id !== id) return i
      const showIssueForm = result === 'ISSUE'
      if (result === 'ISSUE') setExpandedItem(id)
      else if (expandedItem === id) setExpandedItem(null)
      return { ...i, result, showIssueForm, issue: result !== 'ISSUE' ? undefined : i.issue }
    }))
  }

  const setIssueField = (id: string, field: keyof InlineIssue, value: string) => {
    setItems(prev => prev.map(i => {
      if (i.id !== id) return i
      return { ...i, issue: { description: '', severity: 'MEDIUM', location: '', assignedToId: '', ...i.issue, [field]: value } }
    }))
  }

  const answered    = items.filter(i => i.result !== null).length
  const total       = items.length
  const issueItems  = items.filter(i => i.result === 'ISSUE')
  const allAnswered = answered === total

  const autoResult = (): InspectionResult => {
    if (items.some(i => i.result === 'ISSUE')) return 'CONDITIONAL'
    return 'PASS'
  }

  const handleSubmit = () => {
    const job = MOCK_JOBS.find(j => j.id === jobId)
    const now = new Date().toISOString()
    const year = new Date().getFullYear()
    const num  = String(MOCK_WHS_INSPECTIONS.length + 1).padStart(3, '0')

    const inspection: WHSInspection = {
      id:               `ins_${Date.now()}`,
      inspectionNumber: `INS-${year}-${num}`,
      type,
      jobId,
      jobNumber:        job?.jobNumber ?? jobId,
      conductedById:    user?.id ?? 'u1',
      conductedByName:  user?.fullName ?? 'Current User',
      conductedAt:      now,
      result:           allAnswered ? autoResult() : null,
      status:           allAnswered ? 'SUBMITTED' : 'DRAFT',
      positiveObservations: positives ? positives.split('\n').filter(Boolean) : [],
      notes:            overallNotes || undefined,
      items: items.map((i, idx) => ({
        id:       `item_${idx}`,
        label:    i.label,
        category: i.category,
        result:   i.result,
        notes:    undefined,
        photoKey: undefined,
      })),
      issues: issueItems
        .filter(i => i.issue?.description)
        .map((i, idx) => ({
          id:            `iss_new_${idx}`,
          description:   i.issue!.description,
          severity:      i.issue!.severity as IssueSeverity,
          category:      i.category,
          location:      i.issue!.location || 'On site',
          assignedToId:  i.issue!.assignedToId || undefined,
          assignedToName: MOCK_EMPLOYEES.find(e => e.id === i.issue!.assignedToId)?.fullName,
          dueDate:       undefined,
          status:        'OPEN' as IssueStatus,
          closeOutNotes: undefined,
          closedAt:      undefined,
          closedByName:  undefined,
        })),
    }
    onSubmit(inspection)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4 flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            {step === 'checklist' && (
              <button onClick={() => setStep('setup')} className="p-1 hover:bg-slate-100 rounded-lg">
                <ArrowLeft className="w-4 h-4 text-slate-400" />
              </button>
            )}
            <div>
              <h2 className="font-semibold text-slate-900">
                {step === 'setup' ? 'Conduct inspection' : INSPECTION_TYPE_META[type].label}
              </h2>
              {step === 'checklist' && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {answered} of {total} items completed
                  {issueItems.length > 0 && ` · ${issueItems.length} issue${issueItems.length !== 1 ? 's' : ''} raised`}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* ── STEP 1: SETUP ─────────────────────────────────── */}
        {step === 'setup' && (
          <div className="p-6 space-y-5 overflow-y-auto">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">Inspection type *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(Object.keys(INSPECTION_TYPE_META) as InspectionType[]).map(t => {
                  const m = INSPECTION_TYPE_META[t]
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-colors ${
                        type === t
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-xl">{m.icon}</span>
                      <div>
                        <p className={`text-sm font-medium ${type === t ? 'text-indigo-700' : 'text-slate-700'}`}>
                          {m.label}
                        </p>
                        <p className="text-xs text-slate-400">{CHECKLISTS[t]?.length ?? 0} checklist items</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Project / job *</label>
              <select
                value={jobId}
                onChange={e => setJobId(e.target.value)}
                className="input w-full text-sm"
              >
                {MOCK_JOBS.map(j => (
                  <option key={j.id} value={j.id}>{j.jobNumber}</option>
                ))}
              </select>
            </div>

            {/* Preview checklist items */}
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Checklist preview — {CHECKLISTS[type]?.length} items
              </p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {CHECKLISTS[type]?.map((item, i) => (
                  <div key={item.id} className="flex items-start gap-2 text-xs text-slate-600">
                    <span className="text-slate-300 font-mono w-5 flex-shrink-0 mt-0.5">{i + 1}.</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStartChecklist}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Start inspection →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: CHECKLIST ─────────────────────────────── */}
        {step === 'checklist' && (
          <>
            {/* Progress bar */}
            <div className="px-6 py-2 border-b flex-shrink-0">
              <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                <span>{answered} answered</span>
                <span>{issueItems.length > 0 ? `${issueItems.length} issue${issueItems.length !== 1 ? 's' : ''} flagged` : 'No issues yet'}</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                  style={{ width: `${total > 0 ? (answered / total) * 100 : 0}%` }}
                />
              </div>
            </div>

            <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
              {items.map((item, idx) => (
                <div key={item.id} className={`px-6 py-4 ${item.result === 'ISSUE' ? 'bg-red-50/40' : item.result === 'OK' ? 'bg-green-50/30' : ''}`}>
                  {/* Item row */}
                  <div className="flex items-start gap-3">
                    <ItemResultIcon result={item.result} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-slate-700 leading-snug">
                          <span className="text-slate-400 font-mono text-xs mr-1.5">{idx + 1}.</span>
                          {item.label}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                          item.category === 'Electrical' ? 'bg-yellow-50 text-yellow-700' :
                          item.category === 'PPE'        ? 'bg-blue-50 text-blue-700' :
                          item.category === 'Traffic'    ? 'bg-teal-50 text-teal-700' :
                          item.category === 'Emergency'  ? 'bg-red-50 text-red-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {item.category}
                        </span>
                      </div>
                      {/* Result buttons */}
                      <div className="flex gap-1.5 mt-2.5">
                        {(['OK', 'ISSUE', 'NA'] as const).map(r => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setItemResult(item.id, r)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-colors ${
                              item.result === r
                                ? r === 'OK'    ? 'border-green-500 bg-green-500 text-white'
                                : r === 'ISSUE' ? 'border-red-500 bg-red-500 text-white'
                                :                 'border-slate-400 bg-slate-400 text-white'
                                : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'
                            }`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>

                      {/* Inline issue form — expands when ISSUE selected */}
                      {item.result === 'ISSUE' && (
                        <div className="mt-3 bg-white rounded-xl border border-red-200 p-4 space-y-3">
                          <p className="text-xs font-semibold text-red-700 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" /> Issue details
                          </p>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Description *</label>
                            <input
                              value={item.issue?.description ?? ''}
                              onChange={e => setIssueField(item.id, 'description', e.target.value)}
                              placeholder="What is the issue?"
                              className="input w-full text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Severity *</label>
                              <select
                                value={item.issue?.severity ?? 'MEDIUM'}
                                onChange={e => setIssueField(item.id, 'severity', e.target.value)}
                                className="input w-full text-sm"
                              >
                                <option value="LOW">Low</option>
                                <option value="MEDIUM">Medium</option>
                                <option value="HIGH">High</option>
                                <option value="CRITICAL">Critical</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Assign to</label>
                              <select
                                value={item.issue?.assignedToId ?? ''}
                                onChange={e => setIssueField(item.id, 'assignedToId', e.target.value)}
                                className="input w-full text-sm"
                              >
                                <option value="">— Unassigned —</option>
                                {MOCK_EMPLOYEES.map(e => (
                                  <option key={e.id} value={e.id}>{e.fullName}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Location</label>
                            <input
                              value={item.issue?.location ?? ''}
                              onChange={e => setIssueField(item.id, 'location', e.target.value)}
                              placeholder="e.g. Pit 5, SE corner"
                              className="input w-full text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Positive observations + notes */}
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
                    <ThumbsUp className="w-4 h-4 text-green-500" /> Positive observations
                  </label>
                  <textarea
                    value={positives}
                    onChange={e => setPositives(e.target.value)}
                    placeholder="Record any good safety practices observed (one per line)..."
                    className="input w-full resize-none h-20 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Overall notes</label>
                  <textarea
                    value={overallNotes}
                    onChange={e => setOverallNotes(e.target.value)}
                    placeholder="Any additional observations or context..."
                    className="input w-full resize-none h-16 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex-shrink-0 bg-slate-50 flex items-center gap-3">
              <div className="flex-1 text-xs text-slate-500">
                {!allAnswered && (
                  <span className="text-amber-600">
                    {total - answered} item{total - answered !== 1 ? 's' : ''} unanswered — saved as draft
                  </span>
                )}
                {allAnswered && issueItems.length > 0 && (
                  <span className="text-red-600">
                    {issueItems.length} issue{issueItems.length !== 1 ? 's' : ''} will be raised
                  </span>
                )}
                {allAnswered && issueItems.length === 0 && (
                  <span className="text-green-600">All items complete — ready to submit</span>
                )}
              </div>
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-white">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
              >
                <Clipboard className="w-4 h-4" />
                {allAnswered ? 'Submit inspection' : 'Save as draft'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// INSPECTION DETAIL VIEW
// ─────────────────────────────────────────────────────────────────

function InspectionDetail({
  inspection: initial, onBack, onCloseIssue,
}: {
  inspection: WHSInspection
  onBack: () => void
  onCloseIssue: (inspectionId: string, issueId: string, notes: string) => void
}) {
  const [inspection, setInspection] = useState(initial)
  const [closingIssue, setClosingIssue] = useState<InspectionIssue | null>(null)
  const [tab, setTab] = useState<'checklist' | 'issues' | 'positives'>('checklist')

  const typeMeta  = INSPECTION_TYPE_META[inspection.type]
  const openCount = inspection.issues.filter(i => i.status !== 'CLOSED').length

  const handleCloseIssue = (issueId: string, notes: string) => {
    setInspection(ins => ({
      ...ins,
      issues: ins.issues.map(i =>
        i.id === issueId
          ? { ...i, status: 'CLOSED' as IssueStatus, closeOutNotes: notes, closedAt: new Date().toISOString(), closedByName: 'Current User' }
          : i
      ),
    }))
    onCloseIssue(inspection.id, issueId, notes)
  }

  const tabs = [
    { id: 'checklist', label: `Checklist (${inspection.items.length})` },
    { id: 'issues',    label: `Issues (${inspection.issues.length})`, alert: openCount > 0 },
    { id: 'positives', label: 'Observations' },
  ]

  return (
    <div className="p-6 space-y-5 max-w-3xl">
      {/* Back */}
      <div className="flex items-center justify-between gap-2">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to inspections
        </button>
        <button onClick={() => generateInspectionReport(inspection)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
          Print report
        </button>
      </div>

      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${typeMeta.bg} text-2xl`}>
              {typeMeta.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-bold text-slate-900">{inspection.inspectionNumber}</span>
                <ResultBadge result={inspection.result} />
                <span className={`text-xs px-2 py-0.5 rounded-full ${typeMeta.bg} ${typeMeta.text}`}>
                  {typeMeta.label}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {formatDateTime(inspection.conductedAt)}
                </span>
                <span className="flex items-center gap-1">
                  <UserCheck className="w-3 h-3" /> {inspection.conductedByName}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {inspection.jobNumber}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-3 border-t divide-x divide-slate-100">
          {[
            { label: 'Items',  value: inspection.items.length,  color: 'text-slate-800' },
            { label: 'Issues', value: inspection.issues.length, color: inspection.issues.length > 0 ? 'text-red-600' : 'text-slate-800' },
            { label: 'Open',   value: openCount,                color: openCount > 0 ? 'text-red-600' : 'text-green-600' },
          ].map(s => (
            <div key={s.label} className="py-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Overall notes */}
        {inspection.notes && (
          <div className="px-5 py-3 border-t text-sm text-slate-600 bg-slate-50">
            {inspection.notes}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            className={`flex-1 text-xs font-medium py-2 rounded-lg transition-colors relative ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
            {t.alert && (
              <span className="absolute top-1 right-2 w-1.5 h-1.5 bg-red-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Tab: Checklist */}
      {tab === 'checklist' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="divide-y divide-slate-100">
            {inspection.items.map((item, idx) => (
              <div key={item.id} className={`flex items-start gap-3 px-5 py-3.5 ${item.result === 'ISSUE' ? 'bg-red-50/50' : ''}`}>
                <span className="text-xs font-mono text-slate-300 w-5 flex-shrink-0 mt-0.5">{idx + 1}</span>
                <ItemResultIcon result={item.result} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700">{item.label}</p>
                  {item.notes && <p className="text-xs text-slate-400 mt-0.5 italic">{item.notes}</p>}
                </div>
                <span className="text-xs text-slate-400 flex-shrink-0">{item.category}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Issues */}
      {tab === 'issues' && (
        <div className="space-y-3">
          {inspection.issues.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <CheckCircle className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No issues raised during this inspection</p>
            </div>
          )}
          {inspection.issues.map(issue => (
            <div key={issue.id} className={`bg-white border rounded-xl p-4 ${SEVERITY_META[issue.severity].border}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <SeverityBadge severity={issue.severity} />
                    <span className={`text-xs px-2 py-0.5 rounded-full ${ISSUE_STATUS_META[issue.status].bg} ${ISSUE_STATUS_META[issue.status].text}`}>
                      {ISSUE_STATUS_META[issue.status].label}
                    </span>
                    <span className="text-xs text-slate-400">{issue.category}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800">{issue.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                    {issue.location && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{issue.location}</span>
                    )}
                    {issue.assignedToName && (
                      <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" />{issue.assignedToName}</span>
                    )}
                  </div>
                  {issue.closeOutNotes && (
                    <div className="mt-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
                      <strong>Close-out:</strong> {issue.closeOutNotes}
                      {issue.closedAt && <span className="ml-2 text-green-500">({formatDate(issue.closedAt)})</span>}
                    </div>
                  )}
                </div>
                {issue.status !== 'CLOSED' && (
                  <button
                    onClick={() => setClosingIssue(issue)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors flex-shrink-0"
                  >
                    <CheckCircle className="w-3.5 h-3.5" /> Close out
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab: Positive observations */}
      {tab === 'positives' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          {inspection.positiveObservations.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No positive observations recorded</p>
          ) : (
            inspection.positiveObservations.map((obs, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                <ThumbsUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{obs}</p>
              </div>
            ))
          )}
        </div>
      )}

      {/* Issue close-out modal */}
      {closingIssue && (
        <CloseOutModal
          issue={closingIssue}
          onClose={() => setClosingIssue(null)}
          onSubmit={handleCloseIssue}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// OPEN ISSUES TRACKER (cross-project)
// ─────────────────────────────────────────────────────────────────

function IssueTracker({
  inspections, onCloseIssue,
}: {
  inspections: WHSInspection[]
  onCloseIssue: (inspectionId: string, issueId: string, notes: string) => void
}) {
  const [severityFilter, setSeverityFilter] = useState<IssueSeverity | 'ALL'>('ALL')
  const [jobFilter, setJobFilter]           = useState<string>('ALL')
  const [closingIssue, setClosingIssue]     = useState<{ issue: InspectionIssue; inspectionId: string } | null>(null)

  // Flatten all open issues across all inspections
  const allOpenIssues = useMemo(() =>
    inspections.flatMap(ins =>
      ins.issues
        .filter(i => i.status !== 'CLOSED')
        .map(i => ({ ...i, inspectionId: ins.id, inspectionNumber: ins.inspectionNumber, jobId: ins.jobId, jobNumber: ins.jobNumber, conductedAt: ins.conductedAt }))
    ).filter(i =>
      (severityFilter === 'ALL' || i.severity === severityFilter) &&
      (jobFilter === 'ALL'      || i.jobId === jobFilter)
    ).sort((a, b) => {
      const order: Record<IssueSeverity, number> = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
      return order[a.severity] - order[b.severity]
    }),
    [inspections, severityFilter, jobFilter]
  )

  const handleCloseSubmit = (issueId: string, notes: string) => {
    if (!closingIssue) return
    onCloseIssue(closingIssue.inspectionId, issueId, notes)
    setClosingIssue(null)
  }

  if (allOpenIssues.length === 0 && severityFilter === 'ALL' && jobFilter === 'ALL') {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
        <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
        <p className="font-medium text-slate-700">No open issues</p>
        <p className="text-sm text-slate-400 mt-1">All inspection issues are closed out</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-3 flex-col sm:flex-row">
        <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value as any)} className="input text-sm">
          <option value="ALL">All severities</option>
          {Object.entries(SEVERITY_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm">
          <option value="ALL">All projects</option>
          {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
        </select>
      </div>

      {/* Issues list */}
      <div className="space-y-2">
        {allOpenIssues.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No open issues match these filters</p>
        ) : (
          allOpenIssues.map(issue => (
            <div key={`${issue.inspectionId}-${issue.id}`}
              className={`bg-white border rounded-xl p-4 ${SEVERITY_META[issue.severity].border}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <SeverityBadge severity={issue.severity} />
                    <span className="text-xs text-slate-400">{issue.category}</span>
                    <span className="text-xs text-slate-400">·</span>
                    <span className="text-xs font-medium text-indigo-600">{issue.jobNumber}</span>
                    <span className="text-xs text-slate-400">·</span>
                    <span className="text-xs text-slate-400">{issue.inspectionNumber}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800">{issue.description}</p>
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-400">
                    {issue.location && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{issue.location}</span>
                    )}
                    {issue.assignedToName && (
                      <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" />{issue.assignedToName}</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatRelative(issue.conductedAt)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setClosingIssue({ issue, inspectionId: issue.inspectionId })}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors flex-shrink-0"
                >
                  <CheckCircle className="w-3.5 h-3.5" /> Close out
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {closingIssue && (
        <CloseOutModal
          issue={closingIssue.issue}
          onClose={() => setClosingIssue(null)}
          onSubmit={handleCloseSubmit}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────



// ─────────────────────────────────────────────────────────────────
// PHASE 5 — WHS INSPECTION REPORT PDF
// Formatted per-inspection report for WHSO records and client audit.
// ─────────────────────────────────────────────────────────────────
function generateInspectionReport(inspection: WHSInspection) {
  const now   = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtDT = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleString('en-AU', { dateStyle:'medium', timeStyle:'short' }) : '—'
  const tm    = INSPECTION_TYPE_META[inspection.type]
  const rm    = inspection.result ? RESULT_META[inspection.result] : RESULT_META.PENDING
  const resultColor = inspection.result === 'PASS' ? '#166534' : inspection.result === 'FAIL' ? '#b91c1c' : inspection.result ? '#92400e' : '#6b7280'

  const itemRows = inspection.items.map((item, i) => {
    const rc = item.result === 'OK' ? '#166534' : item.result === 'ISSUE' ? '#b91c1c' : '#6b7280'
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 8px;font-size:9.5pt;color:#555">${item.category ?? '—'}</td>
      <td style="padding:5px 10px">${item.label}</td>
      <td style="padding:5px 8px;text-align:center;font-weight:bold;color:${rc}">${item.result === 'OK' ? '✓ OK' : item.result === 'ISSUE' ? '⚠ ISSUE' : item.result === 'NA' ? 'N/A' : '—'}</td>
      <td style="padding:5px 10px;font-size:9.5pt;color:#555;font-style:italic">${item.notes ?? ''}</td>
    </tr>`
  }).join('')

  const issueRows = inspection.issues.map((iss, i) => {
    const sev = { CRITICAL:'#b91c1c', HIGH:'#c2410c', MEDIUM:'#b45309', LOW:'#1d4ed8' }[iss.severity] ?? '#111'
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 8px;font-weight:bold;color:${sev}">${iss.severity}</td>
      <td style="padding:5px 10px">${iss.description}</td>
      <td style="padding:5px 8px;font-size:9pt">${iss.location ?? '—'}</td>
      <td style="padding:5px 8px;font-size:9pt">${iss.assignedToName ?? '—'}</td>
      <td style="padding:5px 8px;text-align:center;font-size:9pt">${iss.dueDate ? new Date(iss.dueDate).toLocaleDateString('en-AU') : '—'}</td>
      <td style="padding:5px 8px;text-align:center;font-weight:bold;color:${iss.status==='CLOSED'?'#166534':'#b45309'}">${iss.status}</td>
    </tr>`
  }).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>WHS Inspection Report — ${inspection.inspectionNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:10.5pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:12px 0 6px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:30%;font-weight:bold;color:#555}
table.items{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:12px}
th{background:#1B3A6B;color:#fff;padding:5px 8px;text-align:left}
.pos-box{background:#f0fdf4;border:1px solid #86efac;border-radius:4px;padding:8px 14px;margin-bottom:10px}
.notes-box{border:1px solid #ccc;border-radius:4px;padding:8px 14px;background:#fafafa;font-size:10.5pt;margin-bottom:10px;min-height:30px}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:28px;margin:12px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right">
    <div style="font-size:16pt;font-weight:900;color:#1B3A6B">WHS INSPECTION REPORT</div>
    <div style="font-family:monospace;font-weight:bold;color:#1B3A6B">${inspection.inspectionNumber}</div>
    <div style="font-size:9pt;color:#888">Generated: ${now}</div>
  </div>
</div>
<div class="info"><table>
  <tr><td>Inspection type</td><td><strong>${tm?.icon ?? ''} ${tm?.label ?? inspection.type.replace(/_/g,' ')}</strong></td>
      <td>Date &amp; time</td><td>${fmtDT(inspection.conductedAt)}</td></tr>
  <tr><td>Job / project</td><td><strong>${inspection.jobNumber}</strong></td>
      <td>Conducted by</td><td>${inspection.conductedByName}</td></tr>
  <tr><td>Overall result</td><td><strong style="font-size:13pt;color:${resultColor}">${rm.label.toUpperCase()}</strong></td>
      <td>Issues raised</td><td>${inspection.issues.length} (${inspection.issues.filter((i: any) => i.status !== 'CLOSED').length} open)</td></tr>
</table></div>

${inspection.issues.length > 0 ? `<h2>Issues raised during inspection</h2>
<table class="items"><thead><tr><th style="width:9%">Severity</th><th style="width:35%">Description</th><th style="width:18%">Location</th><th style="width:16%">Assigned to</th><th style="width:12%;text-align:center">Due</th><th style="width:10%;text-align:center">Status</th></tr></thead>
<tbody>${issueRows}</tbody></table>` : '<p style="color:#166534;font-weight:bold;margin-bottom:10px">✅ No issues raised during this inspection.</p>'}

<h2>Inspection checklist</h2>
<table class="items"><thead><tr><th style="width:14%">Category</th><th style="width:52%">Checklist item</th><th style="width:10%;text-align:center">Result</th><th style="width:24%">Notes</th></tr></thead>
<tbody>${itemRows || '<tr><td colspan="4" style="padding:10px;color:#9ca3af;text-align:center">No checklist items recorded</td></tr>'}</tbody></table>

${inspection.positiveObservations?.length > 0 ? `<h2>Positive observations</h2>
<div class="pos-box"><ul style="margin:0;padding-left:18px">${inspection.positiveObservations.map((o: string) => `<li style="margin:3px 0">${o}</li>`).join('')}</ul></div>` : ''}

${inspection.notes ? `<h2>Inspector notes</h2><div class="notes-box">${inspection.notes}</div>` : ''}

<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Inspector (WHSO / supervisor) — ${inspection.conductedByName} · date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Project Manager — reviewed &amp; accepted</div></div>
</div>
<div class="footer">MCB Civil Electrical · WHS Inspection Report · ${inspection.inspectionNumber} · ${inspection.jobNumber} · ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1020,height=800')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 5 — OPEN ISSUES REGISTER (all jobs, CSV)
// ─────────────────────────────────────────────────────────────────
function exportOpenIssuesCSV(inspections: WHSInspection[]) {
  const fmtDT = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleString('en-AU', { dateStyle:'short', timeStyle:'short' }) : '—'
  const rows: string[][] = [
    ['Inspection Number','Job','Inspection Type','Inspection Date',
     'Issue Severity','Category','Description','Location',
     'Assigned To','Due Date','Status','Close-Out Notes','Closed Date']
  ]
  for (const ins of inspections) {
    for (const iss of ins.issues) {
      rows.push([
        ins.inspectionNumber, ins.jobNumber,
        (ins.type ?? '').replace(/_/g,' '),
        fmtDT(ins.conductedAt),
        iss.severity ?? '', iss.category ?? '', iss.description ?? '',
        iss.location ?? '', iss.assignedToName ?? '',
        iss.dueDate ? new Date(iss.dueDate).toLocaleDateString('en-AU') : '—',
        iss.status ?? '', iss.closeOutNotes ?? '',
        iss.closedAt ? new Date(iss.closedAt).toLocaleDateString('en-AU') : '—',
      ])
    }
  }
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join(String.fromCharCode(10))
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = `MCB_WHS_Issues_Register_${new Date().toISOString().slice(0,10)}.csv`
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}
export function WHSInspections() {
  const { isTier } = useAuthStore()

  const [inspections, setInspections]   = useState<WHSInspection[]>(MOCK_WHS_INSPECTIONS)
  const [apiSeeded, setApiSeeded]       = useState(false)

  // Live inspections from Neon — seeded into state on first load
  const { data: apiInspections = [] } = useQuery({
    queryKey: ['whs-inspections'],
    queryFn:  () => fetchWHSInspections(),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (apiInspections.length > 0 && !apiSeeded) {
      const apiIds = new Set(apiInspections.map((i: any) => i.id))
      setInspections([
        ...(apiInspections as unknown as WHSInspection[]),
        ...MOCK_WHS_INSPECTIONS.filter(i => !apiIds.has(i.id)),
      ])
      setApiSeeded(true)
    }
  }, [apiInspections, apiSeeded])
  const [tab, setTab]                   = useState<'inspections' | 'issues'>('inspections')
  const [selected, setSelected]         = useState<WHSInspection | null>(null)
  const [showConduct, setShowConduct]   = useState(false)
  const [search, setSearch]             = useState('')
  const [typeFilter, setTypeFilter]     = useState<InspectionType | 'ALL'>('ALL')
  const [resultFilter, setResultFilter] = useState<InspectionResult | 'ALL'>('ALL')
  const [jobFilter, setJobFilter]       = useState<string>('ALL')

  const canConduct = isTier('D_FIELD')   // All tiers can conduct inspections

  // ── Derived stats ──────────────────────────────────────────────
  const stats = useMemo(() => {
    const allIssues = inspections.flatMap(i => i.issues)
    const today = new Date().toDateString()
    return {
      today:    inspections.filter(i => new Date(i.conductedAt).toDateString() === today).length,
      openIssues: allIssues.filter(i => i.status !== 'CLOSED').length,
      critical:   allIssues.filter(i => i.severity === 'CRITICAL' && i.status !== 'CLOSED').length,
      passRate:   inspections.length > 0
        ? Math.round((inspections.filter(i => i.result === 'PASS').length / inspections.filter(i => i.result).length) * 100) || 0
        : 0,
    }
  }, [inspections])

  // ── Filtered inspections ───────────────────────────────────────
  const filteredInspections = useMemo(() => inspections.filter(ins => {
    const s = search.toLowerCase()
    const matchSearch = !s ||
      ins.inspectionNumber.toLowerCase().includes(s) ||
      ins.jobNumber.toLowerCase().includes(s) ||
      ins.conductedByName.toLowerCase().includes(s)
    const matchType   = typeFilter   === 'ALL' || ins.type   === typeFilter
    const matchResult = resultFilter === 'ALL' || ins.result === resultFilter
    const matchJob    = jobFilter    === 'ALL' || ins.jobId  === jobFilter
    return matchSearch && matchType && matchResult && matchJob
  }).sort((a, b) => new Date(b.conductedAt).getTime() - new Date(a.conductedAt).getTime()),
    [inspections, search, typeFilter, resultFilter, jobFilter]
  )

  // ── Handlers ──────────────────────────────────────────────────
  const handleNewInspection = (inspection: WHSInspection) => {
    setInspections(prev => [inspection, ...prev])
  }

  const handleCloseIssue = (inspectionId: string, issueId: string, notes: string) => {
    setInspections(prev => prev.map(ins =>
      ins.id !== inspectionId ? ins : {
        ...ins,
        issues: ins.issues.map(i =>
          i.id !== issueId ? i : {
            ...i, status: 'CLOSED', closeOutNotes: notes,
            closedAt: new Date().toISOString(), closedByName: 'Current User',
          }
        ),
      }
    ))
  }

  // Show detail view if an inspection is selected
  if (selected) {
    return (
      <InspectionDetail
        inspection={selected}
        onBack={() => setSelected(null)}
        onCloseIssue={(inspId, issId, notes) => {
          handleCloseIssue(inspId, issId, notes)
          // Also update selected state
          setSelected(prev => prev ? {
            ...prev,
            issues: prev.issues.map(i =>
              i.id !== issId ? i : { ...i, status: 'CLOSED', closeOutNotes: notes, closedAt: new Date().toISOString(), closedByName: 'Current User' }
            ),
          } : null)
        }}
      />
    )
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">HSEQ Inspections</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Safety walk inspections, pre-starts, issue tracking and close-out
          </p>
        </div>
        <button onClick={() => exportOpenIssuesCSV(inspections)}
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            Export issues
          </button>
          {canConduct && (
          <button
            onClick={() => setShowConduct(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> Conduct inspection
          </button>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Today's inspections", value: stats.today,      color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Open issues',          value: stats.openIssues, color: 'text-red-600',    bg: 'bg-red-50',
            onClick: () => setTab('issues') },
          { label: 'Critical open',        value: stats.critical,   color: 'text-orange-600', bg: 'bg-orange-50',
            onClick: () => setTab('issues') },
          { label: 'Pass rate',            value: `${stats.passRate}%`, color: stats.passRate >= 70 ? 'text-green-600' : 'text-amber-600',
            bg: stats.passRate >= 70 ? 'bg-green-50' : 'bg-amber-50' },
        ].map(s => (
          <div
            key={s.label}
            onClick={s.onClick}
            className={`${s.bg} rounded-xl p-4 ${s.onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
          >
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {[
          { id: 'inspections', label: `Inspections (${inspections.length})` },
          { id: 'issues',      label: `Open Issues (${stats.openIssues})`, alert: stats.openIssues > 0 },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            className={`flex-1 flex items-center justify-center gap-2 text-sm font-medium py-2 rounded-lg transition-colors relative ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
            {t.alert && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
          </button>
        ))}
      </div>

      {/* ── TAB: INSPECTIONS ─────────────────────────────────── */}
      {tab === 'inspections' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search inspection number, job, inspector..."
                className="input pl-9 w-full text-sm"
              />
            </div>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="input text-sm">
              <option value="ALL">All types</option>
              {(Object.keys(INSPECTION_TYPE_META) as InspectionType[]).map(t => (
                <option key={t} value={t}>{INSPECTION_TYPE_META[t].icon} {INSPECTION_TYPE_META[t].label}</option>
              ))}
            </select>
            <select value={resultFilter} onChange={e => setResultFilter(e.target.value as any)} className="input text-sm">
              <option value="ALL">All results</option>
              <option value="PASS">Pass</option>
              <option value="CONDITIONAL">Conditional</option>
              <option value="FAIL">Fail</option>
            </select>
            <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm">
              <option value="ALL">All projects</option>
              {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
            </select>
          </div>

          {/* Inspection cards */}
          {filteredInspections.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-200" />
              <p className="font-medium">No inspections found</p>
              <p className="text-sm mt-1">Conduct your first inspection using the button above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredInspections.map(ins => {
                const typeMeta  = INSPECTION_TYPE_META[ins.type]
                const openCount = ins.issues.filter(i => i.status !== 'CLOSED').length
                return (
                  <div
                    key={ins.id}
                    onClick={() => setSelected(ins)}
                    className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-indigo-200 cursor-pointer transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeMeta.bg} text-xl`}>
                        {typeMeta.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">{ins.inspectionNumber}</span>
                          <ResultBadge result={ins.result} />
                          <span className={`text-xs px-2 py-0.5 rounded-full ${typeMeta.bg} ${typeMeta.text}`}>
                            {typeMeta.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(ins.conductedAt)}</span>
                          <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" />{ins.conductedByName}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ins.jobNumber}</span>
                        </div>
                        {/* Issue summary chips */}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-slate-400">{ins.items.length} items</span>
                          {openCount > 0 && (
                            <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded-full font-medium">
                              <AlertTriangle className="w-3 h-3" /> {openCount} open issue{openCount !== 1 ? 's' : ''}
                            </span>
                          )}
                          {ins.positiveObservations.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                              <ThumbsUp className="w-3 h-3" /> {ins.positiveObservations.length} positive
                            </span>
                          )}
                          {ins.status === 'DRAFT' && (
                            <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Draft</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: OPEN ISSUES ─────────────────────────────────── */}
      {tab === 'issues' && (
        <IssueTracker inspections={inspections} onCloseIssue={handleCloseIssue} />
      )}

      {/* Conduct inspection modal */}
      {showConduct && (
        <ConductInspectionModal
          onClose={() => setShowConduct(false)}
          onSubmit={handleNewInspection}
        />
      )}
    </div>
  )
}
